<script setup lang="ts">
import AppGrid from "@/components/AppGrid.vue";
import { FITTABLE_DEFAULT_COL_DEF } from "@/components/composables/useGridColumnDefaults.ts";
import type { ColDef } from "ag-grid-community";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import DashboardStateView from "../components/dashboard/DashboardStateView.vue";
import { derivePendingTransfers } from "../features/transfers/transferRows.ts";
import type { MovementGridRow } from "../redistribution/models/MovementGridRow.ts";
import { useRedistributionStore } from "../stores/redistributionStore.ts";

const redistributionStore = useRedistributionStore();
const {
  redistributionResult,
  itemMovementsGridRowData,
  updatedPOs,
  originalPOs,
} = storeToRefs(redistributionStore);

const allMovements = computed(() => itemMovementsGridRowData.value);

const transferRows = computed(() =>
  derivePendingTransfers({
    movementRows: itemMovementsGridRowData.value,
    updatedPOs: updatedPOs.value,
    originalPOs: originalPOs.value,
  })
);

type TabId = "all" | "pending";
const activeTab = ref<TabId>("pending");

interface ReasonBadge {
  cssClass: string;
  label: string;
}

function getReasonBadge(reason: MovementGridRow["reason"]): ReasonBadge {
  switch (reason) {
    case "CriticalRisk": {
      return {
        cssClass: "item-movements-reason-critical-risk",
        label: "Critical Stockout Prevention",
      };
    }
    case "ModerateRisk": {
      return {
        cssClass: "item-movements-reason-moderate-risk",
        label: "Moderate Stockout Prevention",
      };
    }
    case "BalanceWos": {
      return {
        cssClass: "item-movements-reason-balance-wos",
        label: "Inventory Balancing",
      };
    }
    default: {
      return { cssClass: "item-movements-reason-makeroom", label: "Make Room" };
    }
  }
}

function formatWos(value: number | null | undefined): string {
  return value == null ? "—" : `${value.toFixed(1)}w`;
}

function formatWosChange(value: number | null | undefined): string {
  if (value == null) return "—";
  return `${value >= 0 ? "+" : ""}${value.toFixed(1)}w`;
}

const allMovementsColumnDefs: ColDef[] = [
  {
    field: "sku",
    headerName: "SKU",
    flex: 0.8,
    minWidth: 70,
    filter: "agTextColumnFilter",
    sort: "asc",
  },
  {
    field: "from",
    headerName: "From PO",
    flex: 0.9,
    minWidth: 72,
    filter: "agTextColumnFilter",
  },
  {
    field: "to",
    headerName: "To PO",
    flex: 0.9,
    minWidth: 72,
    filter: "agTextColumnFilter",
  },
  {
    field: "qty",
    headerName: "Qty",
    flex: 0.6,
    minWidth: 60,
    filter: "agNumberColumnFilter",
  },
  {
    field: "reason",
    headerName: "Reason",
    flex: 1.2,
    minWidth: 100,
    filter: "agTextColumnFilter",
    cellRenderer: (params: { value: MovementGridRow["reason"] }) => {
      const badge = getReasonBadge(params.value);
      const span = document.createElement("span");
      span.className = badge.cssClass;
      span.textContent = badge.label;
      return span;
    },
  },
  {
    field: "wosBefore",
    headerName: "WOS Before",
    flex: 0.8,
    minWidth: 72,
    valueFormatter: (params: { value: number | null | undefined }) =>
      formatWos(params.value),
  },
  {
    field: "wosAfter",
    headerName: "WOS After",
    flex: 0.8,
    minWidth: 72,
    valueFormatter: (params: { value: number | null | undefined }) =>
      formatWos(params.value),
  },
  {
    field: "wosChange",
    headerName: "WOS Change",
    flex: 0.8,
    minWidth: 72,
    cellRenderer: (params: { value: number | null | undefined }) => {
      const span = document.createElement("span");
      if (params.value == null) {
        span.className = "dashboard-text-secondary";
        span.textContent = "—";
      } else if (params.value >= 0) {
        span.className = "item-movements-wos-gain";
        span.textContent = formatWosChange(params.value);
      } else {
        span.className = "item-movements-wos-loss";
        span.textContent = formatWosChange(params.value);
      }
      return span;
    },
  },
];

const defaultColDef: ColDef = {
  ...FITTABLE_DEFAULT_COL_DEF,
  filter: false,
};

const pendingTransfersColumnDefs: ColDef[] = [
  {
    field: "sku",
    headerName: "SKU",
    flex: 1,
    minWidth: 70,
    filter: "agTextColumnFilter",
    sort: "asc",
  },
  {
    field: "fromPo",
    headerName: "From PO",
    flex: 1,
    minWidth: 72,
    filter: "agTextColumnFilter",
  },
  {
    field: "toPo",
    headerName: "To PO",
    flex: 1,
    minWidth: 72,
    filter: "agTextColumnFilter",
  },
  {
    field: "qty",
    headerName: "Qty",
    flex: 0.7,
    minWidth: 60,
    filter: "agNumberColumnFilter",
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    minWidth: 80,
    filter: "agTextColumnFilter",
  },
];
</script>

<template>
  <div class="page-main-container gap-5">
    <!-- Page title and subtitle live in AppHeader (route meta). This line stays
         because it is run-dependent, not a static page description. -->
    <section class="dashboard-card rounded-[1.5rem] px-6 py-4">
      <p class="dashboard-text-secondary text-sm leading-6">
        <template v-if="redistributionResult">
          {{ allMovements.length }} movements total &mdash; review all SKU
          movements or filter to pending transfers.
        </template>
        <template v-else>
          Run PO Recommendations to generate redistribution results before
          viewing transfers.
        </template>
      </p>
    </section>

    <!-- No result state -->
    <DashboardStateView
      v-if="!redistributionResult"
      tone="empty"
      title="No redistribution result available"
      message="Run PO Recommendations to generate redistribution results before viewing transfers."
    />

    <template v-else>
      <!-- Tab toggle -->
      <div class="dashboard-card flex gap-1 rounded-[1rem] px-3 py-3">
        <button
          type="button"
          class="rounded-lg px-4 py-2 text-sm font-semibold transition-colors"
          :class="
            activeTab === 'all'
              ? 'dashboard-sidebar-link--active border border-white/20'
              : 'dashboard-sidebar-link--inactive border border-transparent'
          "
          @click="activeTab = 'all'"
        >
          ✅ All Movements
          <span class="ml-2 rounded-full px-2 py-0.5 text-xs opacity-70">
            {{ allMovements.length }}
          </span>
        </button>
        <button
          type="button"
          class="rounded-lg px-4 py-2 text-sm font-semibold transition-colors"
          :class="
            activeTab === 'pending'
              ? 'dashboard-sidebar-link--active border border-white/20'
              : 'dashboard-sidebar-link--inactive border border-transparent'
          "
          @click="activeTab = 'pending'"
        >
          ↔ Pending Transfers
          <span class="ml-2 rounded-full px-2 py-0.5 text-xs opacity-70">
            {{ transferRows.length }}
          </span>
        </button>
      </div>

      <!-- ── All Movements tab ── -->
      <template v-if="activeTab === 'all'">
        <DashboardStateView
          v-if="allMovements.length === 0"
          tone="empty"
          title="No movements found"
          message="The latest redistribution result contains no item movements."
        />

        <section
          v-else
          class="dashboard-card min-w-0 overflow-hidden rounded-[1.5rem]"
        >
          <AppGrid
            id="transfers-all-movements-grid"
            :column-defs="allMovementsColumnDefs"
            :row-data="allMovements"
            :default-col-def="defaultColDef"
            dom-layout="autoHeight"
            :max-rows="100"
            :no-rows-overlay-component-params="{
              getTitle: () => 'No movements found',
            }"
            show-top-border
          />
        </section>
      </template>

      <!-- ── Pending Transfers tab ── -->
      <template v-else>
        <DashboardStateView
          v-if="transferRows.length === 0"
          tone="empty"
          title="No pending or in-progress transfers"
          message="The latest redistribution result does not contain transfers whose recipient PO status is pending or in progress."
        />

        <section
          v-else
          class="dashboard-card min-w-0 overflow-hidden rounded-[1.5rem]"
        >
          <AppGrid
            id="transfers-pending-grid"
            :column-defs="pendingTransfersColumnDefs"
            :row-data="transferRows"
            :default-col-def="defaultColDef"
            dom-layout="autoHeight"
            :max-rows="100"
            :no-rows-overlay-component-params="{
              getTitle: () => 'No pending transfers',
            }"
            show-top-border
          />
        </section>
      </template>
    </template>
  </div>
</template>
